import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, parseISO, startOfDay, endOfDay } from 'date-fns';
import { 
  Clock, MapPin, Camera, AlertTriangle, QrCode, LogIn, LogOut,
  Loader2, Download, Search, X, Filter, ExternalLink, ChevronDown,
  Eye, Calendar as CalendarIcon, Users, Building, RefreshCw
} from 'lucide-react';
import { api } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useToast } from '../../hooks/use-toast';

// Activity type config
const ACTIVITY_TYPES = {
  clock_in: {
    label: 'Clock In',
    icon: LogIn,
    color: 'bg-green-100 text-green-700 border-green-200',
    iconColor: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  clock_out: {
    label: 'Clock Out',
    icon: LogOut,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  qr_scan: {
    label: 'QR Scan',
    icon: QrCode,
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  patrol_photo: {
    label: 'Patrol Photo',
    icon: Camera,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  incident: {
    label: 'Incident',
    icon: AlertTriangle,
    color: 'bg-red-100 text-red-700 border-red-200',
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
  },
};

// Activity Card Component
const ActivityCard = ({ activity, onViewPhoto }) => {
  const config = ACTIVITY_TYPES[activity.type] || ACTIVITY_TYPES.clock_in;
  const Icon = config.icon;
  
  const timestamp = activity.timestamp ? parseISO(activity.timestamp) : null;
  
  return (
    <div 
      className={`relative p-4 rounded-xl border transition-all hover:shadow-md ${config.bgColor} border-slate-200`}
      data-testid={`activity-card-${activity.id}`}
    >
      <div className="flex gap-4">
        {/* Icon */}
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.color.split(' ')[0]}`}>
          <Icon className={`w-6 h-6 ${config.iconColor}`} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <Badge variant="outline" className={`text-xs mb-1 ${config.color}`}>
                {config.label}
              </Badge>
              <p className="font-semibold text-slate-800">{activity.description}</p>
            </div>
            
            {/* Maps Link */}
            {activity.maps_link && (
              <a
                href={activity.maps_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded-lg"
              >
                <MapPin className="w-3 h-3" />
                View Map
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          
          {/* Meta Info */}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {activity.guard_name}
            </span>
            {activity.site_name && (
              <span className="flex items-center gap-1">
                <Building className="w-3.5 h-3.5" />
                {activity.site_name}
              </span>
            )}
            {activity.checkpoint_name && (
              <span className="flex items-center gap-1">
                <QrCode className="w-3.5 h-3.5" />
                {activity.checkpoint_name}
              </span>
            )}
            {timestamp && (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {format(timestamp, 'h:mm a')}
              </span>
            )}
          </div>
          
          {/* Extra Info */}
          {activity.is_late && (
            <div className="mt-2">
              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                Late by {activity.late_minutes} min
              </Badge>
            </div>
          )}
          
          {activity.severity && (
            <div className="mt-2">
              <Badge variant="outline" className={`text-xs ${
                activity.severity === 'critical' ? 'bg-red-100 text-red-700 border-red-300' :
                activity.severity === 'high' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                activity.severity === 'medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                'bg-slate-100 text-slate-600 border-slate-300'
              }`}>
                {activity.severity.toUpperCase()}
              </Badge>
            </div>
          )}
          
          {/* Photo thumbnail */}
          {activity.type === 'patrol_photo' && activity.photo && (
            <button
              onClick={() => onViewPhoto(activity)}
              className="mt-3 relative group overflow-hidden rounded-lg"
            >
              <img
                src={activity.photo.startsWith('data:') ? activity.photo : `data:image/jpeg;base64,${activity.photo}`}
                alt="Patrol"
                className="h-20 w-32 object-cover rounded-lg border border-slate-200 group-hover:opacity-90 transition-opacity"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                <Eye className="w-6 h-6 text-white" />
              </div>
            </button>
          )}
          
          {/* Address */}
          {activity.address && (
            <p className="mt-2 text-xs text-slate-400">{activity.address}</p>
          )}
          
          {/* Coordinates */}
          {activity.latitude && activity.longitude && (
            <p className="mt-1 text-xs text-slate-400 font-mono">
              {activity.latitude.toFixed(6)}, {activity.longitude.toFixed(6)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Filter Chip
const FilterChip = ({ label, active, onClick, count, icon: Icon }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
      active 
        ? 'bg-blue-600 text-white shadow-md' 
        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
    }`}
  >
    {Icon && <Icon className="w-3.5 h-3.5" />}
    {label}
    {count !== undefined && count > 0 && (
      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
        active ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-500'
      }`}>
        {count}
      </span>
    )}
  </button>
);

export default function AdminActivityTimeline() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({});
  const [guards, setGuards] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [dateRange, setDateRange] = useState({
    start: format(new Date(), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });
  const [selectedGuard, setSelectedGuard] = useState('all');
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [timelineData, guardsData, sitesData] = await Promise.all([
        api.getActivityTimeline({
          start_date: dateRange.start,
          end_date: dateRange.end,
          guard_id: selectedGuard !== 'all' ? selectedGuard : undefined,
          site_id: selectedSite !== 'all' ? selectedSite : undefined,
          activity_type: selectedType !== 'all' ? selectedType : undefined,
          limit: 200,
        }),
        api.getGuards(),
        api.getSites(),
      ]);
      
      setActivities(timelineData.activities || []);
      setStats(timelineData.stats || {});
      setGuards(Array.isArray(guardsData) ? guardsData : []);
      setSites(Array.isArray(sitesData) ? sitesData : []);
    } catch (error) {
      console.error('Error loading timeline:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to load activity timeline",
      });
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedGuard, selectedSite, selectedType, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter activities by search
  const filteredActivities = useMemo(() => {
    if (!searchQuery) return activities;
    const query = searchQuery.toLowerCase();
    return activities.filter(a => 
      a.guard_name?.toLowerCase().includes(query) ||
      a.site_name?.toLowerCase().includes(query) ||
      a.checkpoint_name?.toLowerCase().includes(query) ||
      a.description?.toLowerCase().includes(query)
    );
  }, [activities, searchQuery]);

  // Group activities by date
  const groupedActivities = useMemo(() => {
    const groups = {};
    filteredActivities.forEach(activity => {
      const date = activity.timestamp ? activity.timestamp.split('T')[0] : 'Unknown';
      if (!groups[date]) groups[date] = [];
      groups[date].push(activity);
    });
    return groups;
  }, [filteredActivities]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const data = await api.exportActivityTimelineCSV({
        start_date: dateRange.start,
        end_date: dateRange.end,
        guard_id: selectedGuard !== 'all' ? selectedGuard : undefined,
        site_id: selectedSite !== 'all' ? selectedSite : undefined,
      });
      
      const blob = new Blob([data.content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({ title: "Export Complete", description: `Downloaded ${data.row_count} activities` });
    } catch (error) {
      toast({ variant: "destructive", title: "Export Failed", description: error.message });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
            <p className="mt-3 text-slate-500">Loading activity timeline...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="activity-timeline-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Activity Timeline</h1>
            <p className="text-slate-500 text-sm">
              Track all guard activity: clock-ins, QR scans, photos, and incidents
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={loadData}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button 
              onClick={handleExportCSV}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 gap-2"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-slate-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-500 rounded-lg">
                  <Clock className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-700">{stats.total || 0}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <LogIn className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-green-700">{stats.clock_ins || 0}</p>
                  <p className="text-xs text-green-600">Clock Ins</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <LogOut className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-blue-700">{stats.clock_outs || 0}</p>
                  <p className="text-xs text-blue-600">Clock Outs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <QrCode className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-purple-700">{stats.qr_scans || 0}</p>
                  <p className="text-xs text-purple-600">QR Scans</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-amber-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500 rounded-lg">
                  <Camera className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-amber-700">{stats.patrol_photos || 0}</p>
                  <p className="text-xs text-amber-600">Photos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-red-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-red-700">{stats.incidents || 0}</p>
                  <p className="text-xs text-red-600">Incidents</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              {/* Date Range */}
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-40"
                />
                <span className="text-slate-400">to</span>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-40"
                />
              </div>

              {/* Guard Filter */}
              <Select value={selectedGuard} onValueChange={setSelectedGuard}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Guards" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Guards</SelectItem>
                  {guards.map((guard) => (
                    <SelectItem key={guard.id} value={guard.id}>
                      {guard.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Site Filter */}
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search activities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Type Filters */}
            <div className="mt-4 flex flex-wrap gap-2">
              <FilterChip 
                label="All" 
                active={selectedType === 'all'} 
                onClick={() => setSelectedType('all')}
                count={stats.total}
              />
              <FilterChip 
                label="Clock In" 
                active={selectedType === 'clock_in'} 
                onClick={() => setSelectedType('clock_in')}
                count={stats.clock_ins}
                icon={LogIn}
              />
              <FilterChip 
                label="Clock Out" 
                active={selectedType === 'clock_out'} 
                onClick={() => setSelectedType('clock_out')}
                count={stats.clock_outs}
                icon={LogOut}
              />
              <FilterChip 
                label="QR Scans" 
                active={selectedType === 'qr_scan'} 
                onClick={() => setSelectedType('qr_scan')}
                count={stats.qr_scans}
                icon={QrCode}
              />
              <FilterChip 
                label="Photos" 
                active={selectedType === 'patrol_photo'} 
                onClick={() => setSelectedType('patrol_photo')}
                count={stats.patrol_photos}
                icon={Camera}
              />
              <FilterChip 
                label="Incidents" 
                active={selectedType === 'incident'} 
                onClick={() => setSelectedType('incident')}
                count={stats.incidents}
                icon={AlertTriangle}
              />
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        <div className="space-y-6">
          {Object.keys(groupedActivities).length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <Clock className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                <p className="text-lg font-medium text-slate-600">No activities found</p>
                <p className="text-sm text-slate-400 mt-1">
                  {searchQuery ? 'Try a different search term' : 'No activity for the selected filters'}
                </p>
              </CardContent>
            </Card>
          ) : (
            Object.entries(groupedActivities).map(([date, dayActivities]) => (
              <div key={date}>
                {/* Date Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-full text-sm font-medium">
                    <CalendarIcon className="w-4 h-4" />
                    {date !== 'Unknown' ? format(parseISO(date), 'EEEE, MMMM d, yyyy') : 'Unknown Date'}
                  </div>
                  <div className="flex-1 h-px bg-slate-200"></div>
                  <Badge variant="outline" className="text-xs">
                    {dayActivities.length} activities
                  </Badge>
                </div>
                
                {/* Activities for this day */}
                <div className="space-y-3 ml-4 pl-4 border-l-2 border-slate-200">
                  {dayActivities.map((activity) => (
                    <ActivityCard 
                      key={activity.id} 
                      activity={activity}
                      onViewPhoto={setSelectedPhoto}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Photo Preview Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-amber-600" />
              Patrol Photo
            </DialogTitle>
          </DialogHeader>
          {selectedPhoto && (
            <div className="space-y-4">
              <img
                src={selectedPhoto.photo?.startsWith('data:') ? selectedPhoto.photo : `data:image/jpeg;base64,${selectedPhoto.photo}`}
                alt="Patrol"
                className="w-full rounded-lg"
              />
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Guard</p>
                  <p className="font-medium">{selectedPhoto.guard_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Site</p>
                  <p className="font-medium">{selectedPhoto.site_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Checkpoint</p>
                  <p className="font-medium">{selectedPhoto.checkpoint_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Time</p>
                  <p className="font-medium">
                    {selectedPhoto.timestamp ? format(parseISO(selectedPhoto.timestamp), 'h:mm a, MMM d') : 'N/A'}
                  </p>
                </div>
                {selectedPhoto.caption && (
                  <div className="col-span-2">
                    <p className="text-slate-500">Caption</p>
                    <p className="font-medium">{selectedPhoto.caption}</p>
                  </div>
                )}
                {selectedPhoto.address && (
                  <div className="col-span-2">
                    <p className="text-slate-500">Address</p>
                    <p className="font-medium">{selectedPhoto.address}</p>
                  </div>
                )}
              </div>
              {selectedPhoto.maps_link && (
                <a
                  href={selectedPhoto.maps_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <MapPin className="w-4 h-4" />
                  Open in Google Maps
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
